import { useState } from "react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Download, FileXls, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import { SUPPLY_TYPES } from "@/lib/incoming";

const TEMPLATE = `Brand,Series,Product Code,Product,Quantity,Unit,Length per roll (m)
Teckwrap,HM,HM08-HD,Black Cherry Ice,6,rolls,18
Teckwrap,RB,RB04-HD,Passionate Purple,5,rolls,18
3M,2080,,Gloss Black,4,meters,`;

function pick(row, keys) {
  for (const k of Object.keys(row)) {
    if (keys.includes(k.trim().toLowerCase())) return row[k];
  }
  return undefined;
}

function normalizeRows(json) {
  return json
    .map((r) => {
      const unitRaw = String(pick(r, ["unit"]) ?? "rolls").trim().toLowerCase();
      const unit = unitRaw.startsWith("m") ? "meters" : "rolls";
      const lpr = Number(pick(r, ["length per roll (m)", "length per roll", "length", "roll length", "length_per_roll_m"]) ?? 0);
      return {
        brand: String(pick(r, ["brand", "manufacturer", "vendor"]) ?? "").trim(),
        series: String(pick(r, ["series"]) ?? "").trim(),
        product_code: String(pick(r, ["product code", "product_code", "code", "sku", "variant sku"]) ?? "").trim(),
        product: String(pick(r, ["product", "name", "colour", "color"]) ?? "").trim(),
        quantity: Number(pick(r, ["quantity", "qty"]) ?? 0),
        unit,
        length_per_roll_m: lpr > 0 ? lpr : null,
      };
    })
    .filter((r) => r.brand || r.series || r.product || r.product_code);
}

export default function ShipmentImportDialog({ onSuccess, open: openProp, onOpenChange, hideTrigger }) {
  const [openState, setOpenState] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  const [reference, setReference] = useState("");
  const [supplier, setSupplier] = useState("");
  const [shippingType, setShippingType] = useState("Sea Freight Restock");
  const [eta, setEta] = useState("");
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setReference(""); setSupplier(""); setShippingType("Sea Freight Restock"); setEta("");
    setRows([]); setFileName(""); setResult(null);
  };

  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v); else setOpenState(v);
    if (!v) reset();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const norm = normalizeRows(json);
      setRows(norm);
      if (norm.length === 0) toast.error("No data rows found. Check the column headers.");
    } catch (err) {
      toast.error("Could not read that file. Use .xlsx or .csv.");
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "incoming-shipment-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async () => {
    if (!reference.trim()) { toast.error("Enter a shipment reference"); return; }
    if (rows.length === 0) { toast.error("Upload a file with line items first"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/purchase-orders/import", {
        reference: reference.trim(),
        supplier: supplier.trim(),
        shipping_type: shippingType,
        eta: eta || null,
        rows,
      });
      setResult(data);
      if (data.po_id) {
        toast.success(`Shipment created — ${data.created_items} line item${data.created_items === 1 ? "" : "s"}`);
        onSuccess?.();
      } else {
        toast.error("No valid rows — every row had an error");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="import-shipment-btn">
            <UploadSimple size={16} /> Import Shipment
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileXls size={20} /> Import Incoming Shipment</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Shipment reference *</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Teckwrap Sea Freight — Aug" data-testid="shipment-reference" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Supplier</Label>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Teckwrap" data-testid="shipment-supplier" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Supply / freight type</Label>
                <Select value={shippingType} onValueChange={setShippingType}>
                  <SelectTrigger data-testid="shipment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUPPLY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">ETA</Label>
                <Input type="date" value={eta} onChange={(e) => setEta(e.target.value)} className="font-mono" data-testid="shipment-eta" />
              </div>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-[11px] text-slate-600 leading-relaxed max-w-md">
                  <div className="font-semibold text-slate-700 mb-0.5">CSV / Excel columns</div>
                  <span className="font-mono text-slate-700">Brand, Series, Product Code, Product, Quantity, Unit (rolls|meters), Length per roll (m)</span>
                  <div className="mt-1 text-slate-500">Rows are matched by <span className="font-semibold">Product Code</span> (e.g. <span className="font-mono">HM08-HD</span>) to the stored SKU. For “6 rolls @ 18m” → Quantity=6, Unit=rolls, Length per roll=18. For “4.00m” → Quantity=4, Unit=meters.</div>
                </div>
                <Button variant="outline" onClick={downloadTemplate} className="gap-2 rounded-md bg-white shrink-0" data-testid="download-shipment-template">
                  <Download size={16} /> Download CSV template
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono tracking-[0.15em] uppercase text-slate-500">Upload filled sheet:</span>
              <label className="inline-flex items-center gap-2 text-xs font-medium h-9 px-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                <UploadSimple size={14} /> Choose .xlsx / .csv
                <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="shipment-file-input" />
              </label>
              {fileName && <span className="text-xs text-slate-500 truncate max-w-[220px]">{fileName}</span>}
            </div>

            {rows.length > 0 && (
              <div className="border border-slate-200 rounded-md">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex justify-between">
                  <span>Preview</span><span>{rows.length} line items</span>
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2">Brand</th>
                        <th className="px-3 py-2">Series</th>
                        <th className="px-3 py-2">Code</th>
                        <th className="px-3 py-2">Product</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2">Unit</th>
                        <th className="px-3 py-2 text-right">Len/roll</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2">{r.brand}</td>
                          <td className="px-3 py-2">{r.series}</td>
                          <td className="px-3 py-2 font-mono">{r.product_code || "—"}</td>
                          <td className="px-3 py-2 font-medium">{r.product}</td>
                          <td className="px-3 py-2 font-mono text-right">{r.quantity}</td>
                          <td className="px-3 py-2">{r.unit}</td>
                          <td className="px-3 py-2 font-mono text-right">{r.length_per_roll_m ? `${r.length_per_roll_m}m` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="shipment-import-result">
            <div className="grid grid-cols-2 gap-0 border border-slate-200 rounded-md overflow-hidden">
              <div className="p-4 border-r border-slate-200">
                <CheckCircle weight="fill" size={18} className="text-emerald-600" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Line items added</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-emerald-700 tabular-nums">{result.created_items}</div>
              </div>
              <div className="p-4">
                <XCircle weight="fill" size={18} className="text-red-600" />
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">Errors</div>
                <div className="font-mono text-3xl font-semibold mt-1 text-red-600 tabular-nums">{result.error_count}</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div>
                <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Warning size={14} /> Rows not imported (logged to Activity Log)</div>
                <div className="border border-red-200 rounded-md max-h-48 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="px-3 py-2 border-b last:border-0 border-red-100 text-xs">
                      <span className="font-mono text-slate-500">Row {e.row}:</span> <span className="text-red-700">{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={onImport} disabled={submitting || !reference.trim() || rows.length === 0} className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="shipment-import-confirm">
                <UploadSimple size={16} /> {submitting ? "Importing…" : `Import ${rows.length || ""} items`}
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="shipment-import-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
